import {
  ChatAlt2Icon,
  DocumentTextIcon,
  GlobeAltIcon,
  PlusIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { Icon } from '../types';
import { delay, tone } from '../../primitives';

/**
 * The agent's knowledge: the handbook, the benefits guide, intranet pages
 * and a Q&A, with a new version of a policy being learned.
 */
export default function KnowledgeSources() {
  const { t } = useLocale('bot-use-cases');
  const { t: tc } = useLocale('chatbots');

  const sources: {
    icon: Icon;
    name: string;
    type: string;
    size: string;
    learning?: boolean;
  }[] = [
    {
      icon: DocumentTextIcon,
      name: t('Parental leave policy 2027.pdf'),
      type: t('File'),
      size: '8,412',
      learning: true,
    },
    {
      icon: DocumentTextIcon,
      name: t('Employee handbook.pdf'),
      type: t('File'),
      size: '64,210',
    },
    {
      icon: DocumentTextIcon,
      name: t('Benefits guide.pdf'),
      type: t('File'),
      size: '21,876',
    },
    {
      icon: GlobeAltIcon,
      name: 'intranet.example.com/policies',
      type: tc('Website'),
      size: '38,540',
    },
    {
      icon: ChatAlt2Icon,
      name: t('Who approves expenses over €500?'),
      type: t('Q&A'),
      size: '212',
    },
  ];

  return (
    <div
      className={clsxm(
        'overflow-hidden rounded-[22px] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)]',
        tone.surface,
        tone.line,
      )}
    >
      <div className='flex flex-wrap items-center justify-between gap-3 border-b border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
        <div>
          <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
            {tc('Knowledge')}
          </p>
          <p className={clsxm('text-xs', tone.quiet)}>
            {t('Last trained today at 9:12')}
          </p>
        </div>
        <div className='flex gap-2'>
          <span
            className={clsxm(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold',
              tone.line,
              tone.ink,
            )}
          >
            <RefreshIcon className='h-3.5 w-3.5' />
            {t('Retrain all')}
          </span>
          <span className='inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white'>
            <PlusIcon className='h-3.5 w-3.5' />
            {t('Add source')}
          </span>
        </div>
      </div>

      <ul className='px-2 py-2'>
        {sources.map(
          ({ icon: SourceIcon, name, type, size, learning }, index) => (
            <li
              key={name}
              className={clsxm(
                styles.rv,
                'flex items-center gap-3 rounded-xl px-3 py-3',
                learning && 'bg-amber-50/70 dark:bg-amber-400/[0.06]',
              )}
              style={delay(150 + index * 90)}
            >
              <span className='flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-400/10 dark:text-primary-300'>
                <SourceIcon className='h-4 w-4' />
              </span>
              <span className='min-w-0 flex-1'>
                <span
                  className={clsxm(
                    'block truncate text-[13px] font-semibold',
                    tone.ink,
                  )}
                >
                  {name}
                </span>
                <span className={clsxm('block text-xs', tone.quiet)}>
                  {type} · {size} {t('characters')}
                </span>
              </span>
              {learning ? (
                <span className='flex flex-none items-center gap-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-400/15 dark:text-amber-300'>
                  <span className='h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500 motion-reduce:animate-none' />
                  {t('Processing')}
                </span>
              ) : (
                <span className='flex-none rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
                  {t('Ready')}
                </span>
              )}
            </li>
          ),
        )}
      </ul>
    </div>
  );
}
